import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { produce } from 'immer';

import { gqlClient } from '@/api/graphql-client';
import proposalKeys from '@/api/queries/proposal/keyFactor';
import { graphql } from '@/gql';
import {
  GetProposalQuery,
  SetProposalCustomReferenceInput,
  SetProposalCustomReferenceMutation,
} from '@/gql/graphql';

const SET_PROPOSAL_CUSTOM_REFERENCE_MUTATION = graphql(`
  mutation SetProposalCustomReference(
    $input: SetProposalCustomReferenceInput!
  ) {
    setProposalCustomReference(input: $input) {
      id
      reference
      customReference
    }
  }
`);

const useSetProposalCustomReference = (args?: {
  proposalId?: string;
  options?: UseMutationOptions<
    SetProposalCustomReferenceMutation,
    unknown,
    SetProposalCustomReferenceInput
  >;
}) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = args?.options ?? {};

  return useMutation({
    mutationFn: (input: SetProposalCustomReferenceInput) => {
      return gqlClient.request(SET_PROPOSAL_CUSTOM_REFERENCE_MUTATION, {
        input,
      });
    },
    onSuccess: (data, variables, context) => {
      if (args?.proposalId) {
        const queryKey = proposalKeys.proposal(args.proposalId);
        // Patch the returned reference straight into the cache so the header
        // and breadcrumb update without waiting on the refetch. The
        // invalidation still runs so the document preview (driven off this
        // query's fetch cycle) re-renders with the new reference.
        queryClient.setQueryData<GetProposalQuery>(queryKey, (old) =>
          old
            ? produce(old, (draft) => {
                draft.proposal.reference =
                  data.setProposalCustomReference.reference;
                draft.proposal.customReference =
                  data.setProposalCustomReference.customReference;
              })
            : old
        );
        void queryClient.invalidateQueries({ queryKey });
      }
      onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export default useSetProposalCustomReference;
